import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Flame, TrendingUp, Trophy, Target, Star, Users, 
  ChevronRight, Zap, Award, Shield, Crown, ArrowRight,
  Sparkles, ExternalLink, MessageCircle, Wallet, Play, Rocket
} from "lucide-react";
import { useApp } from "../contexts/AppContext";
import { DEMO } from "../constants/mockData";
import { DISCORD_INVITE, BURNZ_LOGO, UEX_REFERRAL } from "../constants";

const LandingPage = () => {
  const navigate = useNavigate();
  const handleLaunchApp = () => navigate('/app');
  
  return (
    <div className="min-h-screen bg-[#0D0D0D]">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 via-transparent to-purple-500/10" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px]" />
        
        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between px-8 py-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img src={BURNZ_LOGO} alt="BURNZ" className="h-12 w-auto" />
            <div>
              <span className="text-2xl font-bold text-white">BURNZ</span>
              <span className="ml-2 text-xs bg-purple-500 text-white px-2 py-0.5 rounded font-semibold">UNION</span>
            </div>
            <Badge className="ml-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 text-xs px-2 py-1 animate-pulse shadow-lg shadow-orange-500/30">
              BETA
            </Badge>
          </div>
          <div className="flex items-center gap-4">
            <a href="#how-it-works" className="text-white/70 hover:text-white transition-colors">How It Works</a>
            <a href="#community" className="text-white/70 hover:text-white transition-colors">Community</a>
            <a href="/whitepaper.html" target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
              📄 Whitepaper
            </a>
            <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white transition-colors flex items-center gap-1">
              <MessageCircle className="w-4 h-4" />Discord
            </a>
            <Button onClick={handleLaunchApp} className="bg-orange-500 hover:bg-orange-600 text-white px-6">
              Launch App
            </Button>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 pt-20 pb-32">
          <div className="max-w-3xl">
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-6 text-sm px-4 py-1">
              🔥 For Rugged NFT Communities
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Got Rugged?<br />
              <span className="bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
                Rise From The Ashes.
              </span>
            </h1>
            <p className="text-xl text-white/60 mb-8 leading-relaxed">
              Turn your worthless NFTs into opportunity. Join thousands of rugged holders who are 
              <span className="text-orange-400 font-semibold"> earning rewards</span>, 
              <span className="text-purple-400 font-semibold"> building community</span>, and 
              <span className="text-cyan-400 font-semibold"> recovering together</span>.
            </p>
            <div className="flex items-center gap-4 mb-12">
              <Button onClick={handleLaunchApp} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white h-14 px-8 text-lg">
                <Wallet className="w-5 h-5 mr-2" />Connect & Start Earning
              </Button>
              <Button variant="outline" size="lg" className="border-white/20 text-white h-14 px-8 hover:bg-white/5">
                <Play className="w-5 h-5 mr-2" />Watch How It Works
              </Button>
            </div>
            
            {/* Stats */}
            <div className="flex items-center gap-8">
              {[
                { value: "45,962", label: "Holders United" },
                { value: "78,366", label: "NFTs Dumped" },
                { value: "156K", label: "BURNZ Burned" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="text-white/40 text-sm">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pain Points Section */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-4">We Know The Feeling</h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              You trusted a project. You believed in the roadmap. Then silence. Empty promises. Worthless JPEGs.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {[
              { 
                emoji: "😤", 
                title: "Abandoned & Forgotten", 
                desc: "Devs disappeared, Discord deleted, left holding bags of worthless NFTs with no closure."
              },
              { 
                emoji: "💸", 
                title: "Money Down The Drain", 
                desc: "Thousands spent on promises that never materialized. Floor price crashed to zero."
              },
              { 
                emoji: "🤝", 
                title: "Community Left Behind", 
                desc: "Fellow holders scattered, no way to organize, no path forward together."
              },
            ].map((item, i) => (
              <Card key={i} className="bg-[#1A1A1A] border-white/5 hover:border-red-500/30 transition-all">
                <CardContent className="p-6 text-center">
                  <span className="text-5xl mb-4 block">{item.emoji}</span>
                  <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-white/50 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 mb-4">The BURNZ Solution</Badge>
            <h2 className="text-3xl font-bold text-white mb-4">Transform Loss Into Opportunity</h2>
            <p className="text-white/50 max-w-2xl mx-auto">
              BURNZ Union brings rugged communities together, giving you a way to earn from your situation and build something new.
            </p>
          </div>
          
          <div className="grid grid-cols-4 gap-6">
            {[
              { 
                step: 1,
                icon: Shield,
                title: "Join a Team Card", 
                desc: "Pick your team and join thousands of holders. Compete on leaderboards together.",
                color: "purple"
              },
              { 
                step: 2,
                icon: Flame,
                title: "Dump Your NFTs", 
                desc: "Send your worthless NFTs to earn XP and BURNZ tokens. Turn trash into treasure.",
                color: "orange"
              },
              { 
                step: 3,
                icon: Target,
                title: "Complete Campaigns", 
                desc: "Simple marketing tasks: Share, Visit, Follow, Review, Refer. Your engagement earns rewards.",
                color: "cyan"
              },
              { 
                step: 4,
                icon: TrendingUp,
                title: "Earn & Grow", 
                desc: "Climb the leaderboards, earn XP, unlock badges, and convert XP to BURNZ tokens.",
                color: "green"
              },
            ].map((item) => (
              <Card key={item.step} className="bg-[#1A1A1A] border-white/5 hover:border-purple-500/30 transition-all group">
                <CardContent className="p-6">
                  <div className={`w-12 h-12 rounded-xl bg-${item.color}-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <item.icon className={`w-6 h-6 text-${item.color}-400`} />
                  </div>
                  <div className="text-white/30 text-sm mb-2">Step {item.step}</div>
                  <h3 className="text-white font-bold mb-2">{item.title}</h3>
                  <p className="text-white/50 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 mb-4">Community Power</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">
                Stronger Together.<br />
                <span className="text-orange-400">Your Community Matters.</span>
              </h2>
              <p className="text-white/50 mb-6">
                The same community that was abandoned now becomes your greatest asset. 
                Join Team Cards, compete on leaderboards, complete marketing campaigns, 
                and turn your collective power into real rewards.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Shield, text: "Join Team Cards for competitive collaboration" },
                  { icon: Target, text: "Complete 5 simple campaign types for XP" },
                  { icon: Flame, text: "BURNZ tokens with deflationary mechanics" },
                  { icon: Award, text: "Leaderboard rewards and exclusive badges" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-orange-400" />
                    </div>
                    <span className="text-white/70">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {/* Show demo teams instead of pools */}
              {[
                { name: 'Dead Apes Recovery', image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400', memberCount: 847 },
                { name: 'Rugged Lions', image: 'https://images.unsplash.com/photo-1614728263952-84ea256f9679?w=400', memberCount: 634 },
                { name: 'Abandoned Lands', image: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400', memberCount: 412 },
                { name: 'Ghost Mint Survivors', image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400', memberCount: 256 },
              ].map((team, i) => (
                <Card key={i} className="bg-[#1A1A1A] border-white/5 overflow-hidden">
                  <div className="h-24 overflow-hidden">
                    <img src={team.image} alt={team.name} className="w-full h-full object-cover" />
                  </div>
                  <CardContent className="p-3">
                    <p className="text-white font-medium text-sm truncate">{team.name}</p>
                    <p className="text-white/40 text-xs">{team.memberCount?.toLocaleString()} members</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-8 text-center">
          <img src={BURNZ_LOGO} alt="BURNZ" className="h-20 w-auto mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Rise?</h2>
          <p className="text-white/50 mb-8 text-lg">
            Join thousands of holders who turned their rugged experience into opportunity. 
            Connect your wallet and start earning today.
          </p>
          <div className="flex items-center justify-center gap-4 mb-6">
            <Button onClick={handleLaunchApp} size="lg" className="bg-orange-500 hover:bg-orange-600 text-white h-14 px-10 text-lg">
              <Rocket className="w-5 h-5 mr-2" />Launch App
            </Button>
            <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="lg" className="border-white/20 text-white h-14 px-8 hover:bg-white/5">
                <MessageCircle className="w-5 h-5 mr-2" />Join Discord
              </Button>
            </a>
          </div>
          <a href={UEX_REFERRAL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm">
            <ExternalLink className="w-4 h-4" />Sign up on UEX.us and start trading
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={BURNZ_LOGO} alt="BURNZ" className="h-8 w-auto" />
            <span className="text-white/50 text-sm">© 2026 BURNZ Union</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="https://twitter.com/BurnzUnion" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm">Twitter</a>
            <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm">Discord</a>
            <a href={UEX_REFERRAL} target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm">UEX.us</a>
            <a href="/whitepaper.html" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white text-sm">Whitepaper</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export { LandingPage };
